import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, getLatestArticles } from '@/mocks/articles';

const categoryImageMap: Record<string, string> = {
  'short-video': 'https://readdy.ai/api/search-image?query=Modern%20short%20video%20production%20studio%2C%20smartphone%20filming%20setup%2C%20creative%20content%20creation%2C%20warm%20lighting%2C%20professional%20equipment&width=800&height=480&seq=cat-short-video&orientation=landscape',
  'social-media': 'https://readdy.ai/api/search-image?query=Social%20media%20marketing%20concept%2C%20digital%20content%20strategy%2C%20modern%20workspace%2C%20warm%20tones%2C%20creative%20professional%20environment&width=800&height=480&seq=cat-social-media&orientation=landscape',
  'creative': 'https://readdy.ai/api/search-image?query=Film%20production%20studio%20with%20professional%20camera%20equipment%2C%20warm%20cinematic%20lighting%2C%20creative%20atmosphere&width=800&height=480&seq=cat-creative&orientation=landscape',
  'branding': 'https://readdy.ai/api/search-image?query=Brand%20strategy%20planning%20session%2C%20creative%20team%20brainstorming%2C%20modern%20office%2C%20warm%20workspace%20atmosphere&width=800&height=480&seq=cat-branding&orientation=landscape',
  'new-media': 'https://readdy.ai/api/search-image?query=New%20media%20operations%20dashboard%2C%20digital%20content%20management%2C%20modern%20workspace%2C%20warm%20professional%20environment&width=800&height=480&seq=cat-new-media&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'short-video');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = getArticlesByCategory(activeSlug);
  const otherArticles = getLatestArticles(6).filter((a) => a.categorySlug !== activeSlug).slice(0, 3);

  return (
    <main className="pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.mitaomedia.com/' },
              { '@type': 'ListItem', position: 2, name: currentCategory?.name || '', item: `https://www.mitaomedia.com/category/${activeSlug}` },
            ],
          }),
        }}
      />

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">{currentCategory?.name}</span>
          </nav>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {currentCategory && (
            <div className="mb-10 p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="w-full md:w-64 h-40 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={categoryImageMap[activeSlug] || categoryImageMap['short-video']}
                    alt={currentCategory.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-heading font-bold text-foreground-900 mb-2">
                    {currentCategory.name}
                  </h4>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:translate-y-[-2px]"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={categoryImageMap[activeSlug] || categoryImageMap['short-video']}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-700 text-xs font-medium">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.date}</span>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {otherArticles.length > 0 && (
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h4 className="text-xl font-heading font-bold text-foreground-900 mb-8">
              更多精彩内容
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {otherArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
                    {article.category}
                  </span>
                  <h5 className="text-sm font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h5>
                  <p className="text-foreground-400 text-xs">
                    {article.date} · {article.readTime}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}